import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import jquery from 'jquery';

import config from './Config';
import * as UserSettingsActions from './Areas/Common/Components/userSettings/userSettings.actions';
import * as FormsApiServiceActions from './Areas/formTemplates/formsApiService.actions';
import * as FormViewerActions from './Areas/formViewer/formViewer.actions';
import * as VideoActions from './Areas/Common/Components/video/video.actions';
import FormEditorWizard from './Areas/formEditor/formEditorWizard.container';
import FormDrawer from './Areas/formDrawer/formDrawer.container';
import FormViewer from './Areas/formViewer/formViewer.container';
import FormSummary from './Areas/formSummary/formSummary.container';
import * as FormsApiService from './Areas/Common/Services/FormsApiService';
import uuidv4 from 'uuid/v4';
import { CitrusTheme } from "@citrus/user";

/**
 * CustomEvent() polyfill
 * https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent#Polyfill
 */
(function () {

  if (typeof window.CustomEvent === 'function') return false;

  function CustomEvent(event, params) {
    params = params || { bubbles: false, cancelable: false, detail: undefined };
    var evt = document.createEvent('CustomEvent');
    evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
    return evt;
  }

  CustomEvent.prototype = window.Event.prototype;

  window.CustomEvent = CustomEvent;
})();


// Set up namespace
if (!window.paylocity) window.paylocity = {};
if (!window.paylocity.talent) window.paylocity.talent = {};
if (!window.paylocity.talent.forms) window.paylocity.talent.forms = {};

// Make sure script hasn't already been loaded
if (!window.paylocity.talent.forms.loaded) {
  // Set up container div
  const container = document.createElement('div');
  const overrides = document.createElement('style');
  container.id = 'TalentForms';
  container.appendChild(overrides);

  // Set up viewport for mobile
  let viewport = window.document.querySelector('meta[name=viewport]');
  if (!viewport) {
    viewport = document.createElement('meta');
    viewport.name = 'viewport';
    window.document.head.appendChild(viewport);
  }

  viewport.content = 'width=device-width, initial-scale=1.0';
  document.body.appendChild(container);
  // set the loaded flag
  window.paylocity.talent.forms.loaded = true;
}


const ViewTypes = {
  empty: 0,
  editor: 1,
  viewer: 2,
  drawer: 3,
  precontent: 4,
  summary: 5,
  viewerDrawer: 6
}

const VideoMode = {
  Player: 0,
  Creator: 1
}

class App extends React.Component {
  
  constructor(props) {
    super(props);

    this.state = {
      activeView: ViewTypes.empty,
      preContent: {},
      params: {},
      viewOptions: undefined,
      editOptions: undefined,
      theme: { name: "legacy" },
    };

    if (!window.$) window.$ = jquery;
    if (!window.jQuery) window.jQuery = jquery;
    window.paylocity.talent.forms.config = config;
  }

  setTheme(theme) 
  {    
    if (theme && theme.name && theme.name !== this.state.theme.name)
    {
      this.setState({theme: theme});   
    }
  }

  componentDidMount() {
    const formsHost = window.paylocity.talent.forms;
    //const startArgs: Array<string> = localSettings.startArgs || [];

    // Register global methods to send to the api
    formsHost.hideAllViews = () => {
      this.setState({ activeView: ViewTypes.empty });
    };

    formsHost.showFormEditor = (co, id, formtype, formid, employeeName, options, theme) => {
      this.setTheme(theme);

      this.setupVideoFeatureIfEnabled(VideoMode.Creator, options, co);

      let params = { companyId: co, employeeId: id, formTypeId: formtype, formId: formid, createdByName: employeeName };
      this.setState({ activeView: ViewTypes.editor, params: params, editOptions: options });
    };

    formsHost.showFormViewer = (co, id, instance, participant, options, theme) => {
      this.setTheme(theme);

      if (!options) options = {};
      const language = options['preferredLocale'] ? options['preferredLocale'] : 'en';
      this.props.userSettingsActions.updateUserSettings({ language: language });
      this.setupVideoFeatureIfEnabled(VideoMode.Player, options, co);

      // setup the mode for the viewer (and clear drawer-mode if set with embedded!)
      if (options['embeddedViewerMode'] && options['drawerViewerMode']) {
        options['drawerViewerMode'] = false;
      }

      let params = { companyId: co, employeeId: id, instanceGuid: instance, instanceParticipantGuid: participant };
      this.setState({ activeView: ViewTypes.viewer, params: params, viewOptions: options });
    };

    formsHost.showFormViewerDrawer = (co, id, instance, participant, options, theme) => {
      this.setTheme(theme);

      if (!options) options = {};
      const language = options['preferredLocale'] ? options['preferredLocale'] : 'en';
      this.props.userSettingsActions.updateUserSettings({ language: language });
      this.setupVideoFeatureIfEnabled(VideoMode.Player, options, co);

      // setup the mode for the drawer (and clear embedded-mode if set!)
      if (options['embeddedViewerMode']) {
        options['embeddedViewerMode'] = false;
      }
      options['drawerViewerMode'] = true;

      let params = { companyId: co, employeeId: id, instanceGuid: instance, instanceParticipantGuid: participant };
      // update the viewerDrawerKey EVERY time this is called so the component is remounted/rendered
      this.setState({ viewerDrawerKey: uuidv4(), activeView: ViewTypes.viewerDrawer, params: params, viewOptions: options });
    };

    formsHost.showSummary = (companyId, employeeId, formInstanceGuid, includeParticipants, isByQuestion, options, viewConfidentially = false, theme) => {
      this.setTheme(theme);

      let params = { companyId, employeeId, formInstanceGuid, includeParticipants, isByQuestion, viewConfidentially };
      this.setState({ activeView: ViewTypes.summary, params: params, viewOptions: options });
    };

    formsHost.showFormDrawer = (co, id, formtype, formId, options, theme) => {
      this.setTheme(theme);

      const language = options && options['preferredLocale'] ? options['preferredLocale'] : 'en';
      this.props.userSettingsActions.updateUserSettings({ language: language });
      this.setupVideoFeatureIfEnabled(VideoMode.Player, options, co);

      let params = { companyId: co, employeeId: id, formTypeId: formtype, formId: formId };
      this.setState({ activeView: ViewTypes.drawer, params: params, viewOptions: options });
    };

    formsHost.showPreContent = (content, theme) => {
      this.setTheme(theme);

      let stringy = JSON.stringify(content, null, 2);
      this.setState({ preContent: stringy, activeView: ViewTypes.precontent });
      return false;
    };

    formsHost.getFormTemplates = (co, id, formtype, theme) => {
      this.setTheme(theme);

      let response = this.props.formsApiServiceActions.getFormTemplates(co, id, formtype);
      return response;
    };

    formsHost.launchFormInstance = (co, id, formInstance, theme) => {
      this.setTheme(theme);

      let response = this.props.formsApiServiceActions.launchFormInstance(co, id, formInstance);
      return response;
    };

    formsHost.getFormInstance = (co, id, instanceId, includeParticipants, theme) => {
      this.setTheme(theme);

      let response = this.props.formsApiServiceActions.getFormInstance(co, id, instanceId, includeParticipants);
      return response;
    };

    formsHost.getFormInstanceWithConfidentiality = (co, id, instanceId, includeParticipants, viewConfidentially, theme) => {
      this.setTheme(theme);

      let response = this.props.formsApiServiceActions.getFormInstanceWithConfidentiality(co, id, instanceId, includeParticipants, viewConfidentially);
      return response;
    };

    formsHost.copyFormTemplate = (co, id, formtype, formid, employeeName, options, theme) => {
      this.setTheme(theme);

      this.setupVideoFeatureIfEnabled(VideoMode.Creator, options, co);

      let params = { companyId: co, employeeId: id, formTypeId: formtype, formId: formid, createdByName: employeeName, action: "copy" };
      this.setState({ activeView: ViewTypes.editor, params: params, editOptions: options });
    };

    formsHost.viewFormTemplate = (co, id, formtype, formid, employeeName, options, theme) => {
      this.setTheme(theme);

      this.setupVideoFeatureIfEnabled(VideoMode.Creator, options, co);

      let params = { companyId: co, employeeId: id, formTypeId: formtype, formId: formid, createdByName: employeeName, action: "view" };
      this.setState({ activeView: ViewTypes.editor, params: params, editOptions: options });
    };

    formsHost.editFormTemplate = (co, id, formtype, formid, employeeName, options, theme) => {
      this.setTheme(theme);
      
      this.setupVideoFeatureIfEnabled(VideoMode.Creator, options, co);

      let params = { companyId: co, employeeId: id, formTypeId: formtype, formId: formid, createdByName: employeeName };
      this.setState({ activeView: ViewTypes.editor, params: params, editOptions: options });
    };

    // EV-1363 - Will mark a formInstance as completed. This is used if the original save required process
    // indicated the calling client needed to perform some post processing before marking the formInstance as complete.
    // In surveys, we use this as a callback after we ensure the PDF of the user response can be saved... That way, we can error
    // out and leave the form as draft, allowing the user to attempt to resubmit successfully.
    formsHost.clientPostProcessingComplete = (formInstance, isPublicForm) => {
      this.props.formViewerActions.completeLaunchedFormAfterPostProcessing(
        formInstance,
        isPublicForm
      );
    };

  }

  isOptionEnabled(option) {
    let enabled = (this.state.viewOptions && this.state.viewOptions[option] === true);
    return enabled;
  }

  setupVideoFeatureIfEnabled(mode, options, companyId) {
    let allowVideoFeature = (options && options["allowVideoFeature"] === true);
     if (!allowVideoFeature) return;

    let isPublicSurvey = (options && options["isPublicSurvey"] === true);

    if (mode === VideoMode.Player) {
      this.props.videoActions.loadVideoPlayer(companyId, isPublicSurvey);
    }
    else {
      this.props.videoActions.loadVideoCreator(companyId);
    }
  }

  renderBody() {    
    if (this.isOptionEnabled('embeddedViewerMode')) {      
      return (
        
        <div className="talent-forms">
          {this.state.activeView === ViewTypes.viewer && <FormViewer params={this.state.params} options={this.state.viewOptions} />}
          {this.state.activeView === ViewTypes.summary && <FormSummary params={this.state.params} options={this.state.viewOptions} />}
        </div>
      );
    }

    else {
      return (
        <div className="talent-forms">
          {this.state.activeView === ViewTypes.editor && <FormEditorWizard params={this.state.params} options={this.state.editOptions}/>}
          {this.state.activeView === ViewTypes.viewer && <FormViewer params={this.state.params} options={this.state.viewOptions} />}
          {this.state.activeView === ViewTypes.viewerDrawer && <FormViewer key={this.state.viewerDrawerKey} params={this.state.params} options={this.state.viewOptions} />}
          {this.state.activeView === ViewTypes.summary && <FormSummary params={this.state.params} options={this.state.viewOptions} />}
          {this.state.activeView === ViewTypes.drawer && <FormDrawer params={this.state.params} options={this.state.viewOptions} />}
          {this.state.activeView === ViewTypes.precontent &&
            <pre style={{ fontFamily: 'unset', margin: '20px', overflow: 'unset', whiteSpace: 'pre-wrap' }}>{this.state.preContent}</pre>}
        </div>
      );
    }
  }

  render() {
    return (
      <CitrusTheme.Provider value={this.state.theme}>
        {this.renderBody()}
      </CitrusTheme.Provider>
    );
  }

}

export default connect(
  (state) => ({ state: state }),
  dispatch => ({
    formsApiServiceActions: bindActionCreators(FormsApiServiceActions, dispatch),
    userSettingsActions: bindActionCreators(UserSettingsActions, dispatch),
    videoActions: bindActionCreators(VideoActions, dispatch),
    formViewerActions: bindActionCreators(FormViewerActions, dispatch),
  })
)(App);



const errorHandler = e => {
  if (e.message === 'ResizeObserver loop limit exceeded') return;

  if (e.reason && e.reason.response && e.reason.response.status === 400 && e.reason.response.statusText === 'Invalid Session') {
    return;
  }

  const fileName = e.filename || '';
  const typeName = e.type || '';

  if ((fileName.toLowerCase().indexOf('/talentforms') >= 0) || typeName.toLowerCase() === 'unhandledrejection') {
    var errorMsg = e.message || (e.error && e.error.message) || (e.reason && e.reason.response && e.reason.response.statusText) || 'Unknown Error detected';
    errorMsg = !!e.error && !!e.error.stack ? errorMsg + " " + e.error.stack : errorMsg;

    var lineNumber = e.lineno;
    var location = window.location.toString().toLowerCase();
    var enableClientLogging = window.location && window.location.search && (window.location.search.indexOf("clientlogging=true") > -1);

    // On page load window.onerror is always called with ($, undefined, undefined) once
    // Checking to see if errorMsg is equal to the jQuery object before showing the error banner
    if (enableClientLogging && $ && errorMsg !== $) {
      try {
        return FormsApiService.logClientMessage(errorMsg, location, lineNumber, true);
      }
      catch(err) {
        console.log(err);
        console.log(e.error);
      }
    }
  }
};

window.addEventListener('error', errorHandler);
window.addEventListener('unhandledrejection', errorHandler);

